import { Users, Zap, Crown } from 'lucide-react';

/**
 * Stripe configuration utility for BrixBot frontend.
 * Fetches all Stripe configuration from backend API for security.
 */

let stripeConfigCache = null;

/**
 * Fetch Stripe configuration from backend API
 */
export const fetchStripeConfig = async () => {
  if (stripeConfigCache) {
    return stripeConfigCache;
  }

  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    const response = await fetch(`${backendUrl}/api/payments/stripe-config`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch Stripe config: ${response.status}`);
    }
    
    stripeConfigCache = await response.json();
    return stripeConfigCache;
  } catch (error) {
    console.error('Error fetching Stripe configuration:', error);
    
    // Fallback to environment variables when backend API fails
    return {
      publishable_key: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
      product_ids: {
        starter_monthly: process.env.REACT_APP_STRIPE_STARTER_MONTHLY_PRODUCT_ID || '',
        starter_yearly: process.env.REACT_APP_STRIPE_STARTER_YEARLY_PRODUCT_ID || '',
        professional_monthly: process.env.REACT_APP_STRIPE_PROFESSIONAL_MONTHLY_PRODUCT_ID || '',
        professional_yearly: process.env.REACT_APP_STRIPE_PROFESSIONAL_YEARLY_PRODUCT_ID || '',
        enterprise_monthly: process.env.REACT_APP_STRIPE_ENTERPRISE_MONTHLY_PRODUCT_ID || '',
        enterprise_yearly: process.env.REACT_APP_STRIPE_ENTERPRISE_YEARLY_PRODUCT_ID || ''
      },
      price_ids: {
        starter_monthly: process.env.REACT_APP_STRIPE_STARTER_MONTHLY_PRICE_ID || '',
        starter_yearly: process.env.REACT_APP_STRIPE_STARTER_YEARLY_PRICE_ID || '',
        professional_monthly: process.env.REACT_APP_STRIPE_PROFESSIONAL_MONTHLY_PRICE_ID || '',
        professional_yearly: process.env.REACT_APP_STRIPE_PROFESSIONAL_YEARLY_PRICE_ID || '',
        enterprise_monthly: process.env.REACT_APP_STRIPE_ENTERPRISE_MONTHLY_PRICE_ID || '',
        enterprise_yearly: process.env.REACT_APP_STRIPE_ENTERPRISE_YEARLY_PRICE_ID || ''
      }
    };
  }
};

/**
 * Get product ID to name mapping
 */
export const getProductIdToNameMap = async () => {
  const config = await fetchStripeConfig();
  return {
    [config.product_ids.starter_monthly]: "Starter Monthly",
    [config.product_ids.starter_yearly]: "Starter Yearly",
    [config.product_ids.professional_monthly]: "Professional Monthly",
    [config.product_ids.professional_yearly]: "Professional Yearly",
    [config.product_ids.enterprise_monthly]: "Enterprise Monthly",
    [config.product_ids.enterprise_yearly]: "Enterprise Yearly",
  };
};

/**
 * Get price ID to name mapping
 */
export const getPriceIdToNameMap = async () => {
  const config = await fetchStripeConfig();
  return {
    [config.price_ids.starter_monthly]: "Starter Monthly",
    [config.price_ids.professional_monthly]: "Professional Monthly",
    [config.price_ids.enterprise_monthly]: "Enterprise Monthly",
    [config.price_ids.starter_yearly]: "Starter Yearly",
    [config.price_ids.professional_yearly]: "Professional Yearly",
    [config.price_ids.enterprise_yearly]: "Enterprise Yearly",
  };
};

/**
 * Get combined plan ID to name mapping (both product and price IDs)
 */
export const getPlanIdToNameMap = async () => {
  const [productMap, priceMap] = await Promise.all([
    getProductIdToNameMap(),
    getPriceIdToNameMap()
  ]);
  return {
    ...productMap,
    ...priceMap,
  };
};

/**
 * Get plans data for components with configuration from backend
 */
export const getPlansData = async () => {
  const config = await fetchStripeConfig();
  
  return [
    {
      id: config.price_ids.starter_monthly,
      name: 'Starter',
      type: 'monthly',
      price: 25,
      period: 'month',
      features: [
        'Up to 1,000 messages/month',
        'Basic AI training',
        'Email support',
        'Standard integrations',
        'Basic analytics'
      ],
      color: 'from-blue-400 to-cyan-500',
      icon: Users,
      popular: false
    },
    {
      id: config.price_ids.starter_yearly,
      name: 'Starter',
      type: 'yearly',
      price: 270,
      period: 'year',
      monthlyEquivalent: 22.50,
      originalPrice: 300,
      features: [
        'Up to 1,000 messages/month',
        'Basic AI training',
        'Email support',
        'Standard integrations',
        'Basic analytics',
        '2 months FREE'
      ],
      color: 'from-blue-400 to-cyan-500',
      icon: Users,
      popular: false
    },
    {
      id: config.price_ids.professional_monthly,
      name: 'Professional',
      type: 'monthly',
      price: 30,
      period: 'month',
      features: [
        'Up to 1,500 messages/month',
        'Advanced AI training (monthly retraining)',
        '24/7 priority support',
        'WhatsApp integration',
        'Advanced analytics (coming soon...)'
      ],
      color: 'from-violet-400 to-purple-500',
      icon: Zap,
      popular: true
    },
    {
      id: config.price_ids.professional_yearly,
      name: 'Professional',
      type: 'yearly',
      price: 324,
      period: 'year',
      monthlyEquivalent: 27,
      originalPrice: 360,
      features: [
        'Up to 1,500 messages/month',
        'Advanced AI training (monthly retraining)',
        '24/7 priority support',
        'WhatsApp integration',
        'Advanced analytics (coming soon...)',
        '2 months FREE'
      ],
      color: 'from-violet-400 to-purple-500',
      icon: Zap,
      popular: true
    },
    {
      id: config.price_ids.enterprise_monthly,
      name: 'Enterprise',
      type: 'monthly',
      price: 60,
      period: 'month',
      features: [
        'Up to 3,000 messages/month',
        'Advanced AI training (monthly retraining)',
        '24/7 priority support',
        'WhatsApp, Messenger & Instagram integration',
        'Advanced analytics (coming soon...)',
        'Custom branding',
        'Dedicated account manager'
      ],
      color: 'from-amber-400 to-orange-500',
      icon: Crown,
      popular: false
    },
    {
      id: config.price_ids.enterprise_yearly,
      name: 'Enterprise',
      type: 'yearly',
      price: 648,
      period: 'year',
      monthlyEquivalent: 54,
      originalPrice: 720,
      features: [
        'Up to 3,000 messages/month',
        'Advanced AI training (monthly retraining)',
        '24/7 priority support',
        'WhatsApp, Messenger & Instagram integration',
        'Advanced analytics (coming soon...)',
        'Custom branding',
        'Dedicated account manager',
        '2 months FREE'
      ],
      color: 'from-amber-400 to-orange-500',
      icon: Crown,
      popular: false
    }
  ];
};

/**
 * Check if plan is Starter tier
 */
export const isStarterPlan = async (selectedPlanId) => {
  const config = await fetchStripeConfig();
  return selectedPlanId === config.price_ids.starter_monthly || 
         selectedPlanId === config.price_ids.starter_yearly ||
         selectedPlanId === config.product_ids.starter_monthly || 
         selectedPlanId === config.product_ids.starter_yearly;
};

/**
 * Check if plan is Professional tier
 */
export const isProfessionalPlan = async (selectedPlanId) => {
  const config = await fetchStripeConfig();
  return selectedPlanId === config.price_ids.professional_monthly || 
         selectedPlanId === config.price_ids.professional_yearly ||
         selectedPlanId === config.product_ids.professional_monthly || 
         selectedPlanId === config.product_ids.professional_yearly;
};

/**
 * Check if plan is Enterprise tier
 */
export const isEnterprisePlan = async (selectedPlanId) => {
  const config = await fetchStripeConfig();
  return selectedPlanId === config.price_ids.enterprise_monthly || 
         selectedPlanId === config.price_ids.enterprise_yearly ||
         selectedPlanId === config.product_ids.enterprise_monthly || 
         selectedPlanId === config.product_ids.enterprise_yearly;
};

export default fetchStripeConfig;